import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import type { Devotional } from './useDevotionals';

export type UpsertDevotionalPayload = Partial<Omit<Devotional, 'id'>> & {
  id?: string; // optional for insert
  content?: string | null;
  thumbnail?: string | null;
  series_id?: string | null;
  day_titles?: string[] | null;
  author?: string | null;
  reading_time?: string | null;
  tags?: string[] | null;
};

export const useUpsertDevotional = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpsertDevotionalPayload) => {
      const { id, ...rest } = payload;
      const cleaned = Object.fromEntries(
        Object.entries(rest).filter(([, value]) => value !== undefined)
      );
      if (id) {
        const { data, error, count } = await supabase
          .from('devotionals')
          .update(cleaned, { count: 'exact' })
          .eq('id', id)
          .select('id')
          .maybeSingle();
        if (error) throw error;
        if (!data) {
          throw new Error(
            count === 0
              ? 'Devotional update failed (no rows updated). Check permissions or row visibility.'
              : 'Devotional update failed (no row returned).'
          );
        }
        return data;
      }
      const { data, error } = await supabase.from('devotionals').insert(cleaned).select().maybeSingle();
      if (error) throw error;
      if (!data) {
        throw new Error('Devotional insert failed (no row returned).');
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devotionals'] });
    },
  });
};
